import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import Layout from '../components/Layout';
import { useAuth, useCan } from '../contexts/AuthContext';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
import {
    ArrowLeft, User, Mail, Phone, MapPin, Truck, Package, Receipt, Loader2, Send, Download, CheckCircle2, XCircle, Pencil,
    Plus, Trash2, CreditCard, Banknote, FileSignature, Zap
} from 'lucide-react';
import { toast } from 'sonner';
import ImportantNotesBanner from '../components/ImportantNotesBanner';
import ImportantNotesButton from '../components/ImportantNotesButton';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STATUT_PAIEMENT = {
    impaye: { label: 'Impayée', cls: 'bg-red-100 text-red-700 border border-red-200' },
    partiellement_paye: { label: 'Partiellement payée', cls: 'bg-orange-100 text-orange-700 border border-orange-200' },
    totalement_paye: { label: 'Totalement payée', cls: 'bg-emerald-100 text-emerald-700 border border-emerald-200' },
};

const PAIEMENT_MODES = [
    { value: 'especes', label: 'Espèces', icon: Banknote },
    { value: 'virement', label: 'Virement', icon: Zap },
    { value: 'cheque_ao', label: 'Chèque AO', icon: FileSignature },
    { value: 'cheque_so', label: 'Chèque SO', icon: FileSignature },
];

function formatDate(d) {
    if (!d) return '—';
    try {
        return new Date(d + 'T00:00').toLocaleDateString('fr-FR');
    } catch {
        return d;
    }
}

function formatEUR(n) {
    const v = Number(n || 0);
    return v.toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' EUR';
}

function normalizeAddress(raw) {
    if (!raw) return '';
    if (typeof raw === 'string') return raw;
    if (typeof raw === 'object') return raw.adresse || '';
    return '';
}

export default function FactureDetail() {
    const { id } = useParams();
    const navigate = useNavigate();
    const { canWrite: canWriteFact } = useCan('facturation');
    const { user } = useAuth();
    const isAdmin = user?.role === 'admin';
    const [facture, setFacture] = useState(null);
    const [client, setClient] = useState(null);
    const [loading, setLoading] = useState(true);
    const [sending, setSending] = useState(false);
    const [downloading, setDownloading] = useState(false);
    const [editOpen, setEditOpen] = useState(false);
    const [editForm, setEditForm] = useState({ nom: '', telephone: '', email: '', adresse: '', prix_ttc: '', lignes: [] });

    // --- Payment form state ---
    const [paymentForm, setPaymentForm] = useState({
        montant: '',
        date: new Date().toISOString().slice(0, 10),
        type_paiement: '',
        remarque: '',
    });
    const [addingPayment, setAddingPayment] = useState(false);

    const fetchData = async () => {
        try {
            const fRes = await axios.get(`${API_URL}/api/factures/${id}`, { withCredentials: true });
            setFacture(fRes.data);
            if (fRes.data?.client_id) {
                const cRes = await axios.get(`${API_URL}/api/clients/${fRes.data.client_id}`, { withCredentials: true });
                setClient(cRes.data);
            }
        } catch (err) {
            toast.error('Erreur chargement facture');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
    }, [id]);

    const handleDownload = async () => {
        setDownloading(true);
        try {
            const res = await axios.get(`${API_URL}/api/factures/${id}/pdf`, {
                withCredentials: true,
                responseType: 'blob',
            });
            const blob = new Blob([res.data], { type: 'application/pdf' });
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `Facture_${facture?.numero || 'LUXY'}.pdf`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            URL.revokeObjectURL(url);
            toast.success('Facture téléchargée');
        } catch {
            toast.error('Erreur lors du téléchargement');
        } finally {
            setDownloading(false);
        }
    };

    const handleSend = async () => {
        if (!facture?.client_email) {
            toast.error("Le client n'a pas d'email renseigné");
            return;
        }
        if (!window.confirm(`Envoyer la facture ${facture.numero} à ${facture.client_email} ?`)) return;
        setSending(true);
        try {
            const res = await axios.post(`${API_URL}/api/factures/${id}/send`, {}, { withCredentials: true });
            toast.success(`Facture envoyée à ${res.data.to}`);
            fetchData();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Échec de l'envoi");
        } finally {
            setSending(false);
        }
    };

    if (loading) {
        return (
            <Layout>
                <div className="flex items-center justify-center py-24">
                    <Loader2 className="w-6 h-6 animate-spin text-[#1B365D]" />
                </div>
            </Layout>
        );
    }

    if (!facture) {
        return (
            <Layout>
                <div className="py-16 text-center text-gray-500">Facture introuvable</div>
            </Layout>
        );
    }

    const paiement = STATUT_PAIEMENT[facture.statut_paiement] || STATUT_PAIEMENT.impaye;
    const addrDepart = normalizeAddress(client?.adresse_depart);
    const addrArrivee = normalizeAddress(client?.adresse_arrivee);
    const selected = facture.selected_adresse || 'depart';
    const displayNom = facture.nom_override || facture.client_nom || '—';
    const displayTel = facture.telephone_override || facture.client_telephone || '';
    const displayEmail = facture.email_override || facture.client_email || '';
    const displayAdresse = facture.adresse_override || (selected === 'arrivee' ? addrArrivee : addrDepart);
    const displayPrixTtc = facture.prix_ttc_override != null ? Number(facture.prix_ttc_override) : Number(facture.prix_ttc || 0);
    const hasOverride = !!(facture.nom_override || facture.telephone_override || facture.email_override || facture.adresse_override || facture.prix_ttc_override != null);

    const setSelectedAddress = async (which) => {
        if (which === selected) return;
        try {
            await axios.patch(`${API_URL}/api/factures/${id}`, { selected_adresse: which }, { withCredentials: true });
            setFacture(prev => ({ ...prev, selected_adresse: which }));
            toast.success(`Adresse ${which === 'depart' ? 'de départ' : "d'arrivée"} sélectionnée`);
        } catch {
            toast.error('Erreur');
        }
    };

    const openEdit = () => {
        setEditForm({
            nom: facture.nom_override ?? (facture.client_nom || ''),
            telephone: facture.telephone_override ?? (facture.client_telephone || ''),
            email: facture.email_override ?? (facture.client_email || ''),
            adresse: facture.adresse_override ?? displayAdresse,
            prix_ttc: facture.prix_ttc_override != null ? String(facture.prix_ttc_override) : String(facture.prix_ttc || ''),
            lignes: Array.isArray(facture.lignes) && facture.lignes.length
                ? facture.lignes.map(l => ({ designation: l.designation || '', tarif_ttc: String(l.tarif_ttc ?? '') }))
                : [],
        });
        setEditOpen(true);
    };

    const addLigne = () => setEditForm(p => ({ ...p, lignes: [...(p.lignes || []), { designation: '', tarif_ttc: '' }] }));
    const updateLigne = (idx, field, value) => setEditForm(p => {
        const next = [...(p.lignes || [])];
        next[idx] = { ...next[idx], [field]: value };
        return { ...p, lignes: next };
    });
    const removeLigne = (idx) => setEditForm(p => ({ ...p, lignes: (p.lignes || []).filter((_, i) => i !== idx) }));

    // Auto-computed TTC from lignes
    const lignesTotal = (editForm.lignes || []).reduce((s, l) => s + (parseFloat(l.tarif_ttc) || 0), 0);
    const hasLignes = (editForm.lignes || []).some(l => (l.designation || '').trim() !== '' || parseFloat(l.tarif_ttc) > 0);

    const saveEdit = async () => {
        try {
            const payload = {
                nom_override: editForm.nom || null,
                telephone_override: editForm.telephone || null,
                email_override: editForm.email || null,
                adresse_override: editForm.adresse || null,
            };
            if (hasLignes) {
                payload.lignes = (editForm.lignes || [])
                    .filter(l => (l.designation || '').trim() !== '' || parseFloat(l.tarif_ttc) > 0)
                    .map(l => ({
                        designation: (l.designation || '').trim(),
                        tarif_ttc: parseFloat(l.tarif_ttc) || 0,
                    }));
                // prix_ttc_override sera calcule automatiquement cote backend
            } else {
                payload.lignes = [];
                payload.prix_ttc_override = editForm.prix_ttc === '' ? null : Number(editForm.prix_ttc);
            }
            await axios.patch(`${API_URL}/api/factures/${id}`, payload, { withCredentials: true });
            toast.success('Informations mises à jour sur la facture');
            setEditOpen(false);
            fetchData();
        } catch {
            toast.error("Erreur lors de l'enregistrement");
        }
    };

    const resetOverrides = async () => {
        try {
            await axios.patch(`${API_URL}/api/factures/${id}`, {
                nom_override: null,
                telephone_override: null,
                email_override: null,
                adresse_override: null,
                prix_ttc_override: null,
            }, { withCredentials: true });
            toast.success('Informations remises aux valeurs du client');
            fetchData();
        } catch {
            toast.error('Erreur');
        }
    };

    // --- Payment handlers ---
    const fillSolde = () => {
        setPaymentForm(p => ({ ...p, montant: Number(facture?.solde || 0).toFixed(2) }));
    };
    const fillPct = (pct) => {
        const total = Number(facture?.prix_ttc || 0);
        setPaymentForm(p => ({ ...p, montant: (total * pct / 100).toFixed(2) }));
    };

    const submitPayment = async () => {
        const m = parseFloat(paymentForm.montant);
        if (!m || m <= 0) { toast.error('Saisir un montant > 0'); return; }
        if (!paymentForm.date) { toast.error('Date requise'); return; }
        if (!paymentForm.type_paiement) { toast.error('Choisir un mode de paiement'); return; }
        setAddingPayment(true);
        try {
            await axios.post(
                `${API_URL}/api/clients/${facture.client_id}/acomptes`,
                {
                    montant: m,
                    date: paymentForm.date,
                    type_paiement: paymentForm.type_paiement,
                    remarque: paymentForm.remarque || 'Paiement',
                },
                { withCredentials: true },
            );
            toast.success('Paiement enregistré');
            setPaymentForm({
                montant: '',
                date: new Date().toISOString().slice(0, 10),
                type_paiement: '',
                remarque: '',
            });
            fetchData();
        } catch (e) {
            toast.error(e?.response?.data?.detail || 'Erreur enregistrement paiement');
        } finally {
            setAddingPayment(false);
        }
    };

    const deletePayment = async (acompteId) => {
        if (!window.confirm('Supprimer ce paiement ?')) return;
        try {
            await axios.delete(
                `${API_URL}/api/clients/${facture.client_id}/acomptes/${acompteId}`,
                { withCredentials: true },
            );
            toast.success('Paiement supprimé');
            fetchData();
        } catch {
            toast.error('Erreur suppression');
        }
    };

    return (
        <Layout>
            <div className="space-y-5" data-testid="facture-detail-page">
                {/* Header */}
                <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
                    <div className="flex items-center gap-3">
                        <button
                            onClick={() => navigate('/facturation')}
                            className="flex items-center gap-1 text-gray-500 hover:text-[#1B365D] transition-colors text-sm"
                            data-testid="facture-back-btn"
                        >
                            <ArrowLeft className="w-4 h-4" /> Retour
                        </button>
                        <div className="flex items-center gap-3">
                            <div className="h-10 w-10 rounded-lg bg-[#1B365D]/10 flex items-center justify-center">
                                <Receipt className="w-5 h-5 text-[#1B365D]" />
                            </div>
                            <div>
                                <h1 className="text-xl font-bold text-[#1B365D]" style={{ fontFamily: 'Outfit, sans-serif' }}>
                                    Facture <span className="font-mono">{facture.numero}</span>
                                </h1>
                                <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                                    <Badge className={`text-[10px] ${paiement.cls}`}>{paiement.label}</Badge>
                                    <ImportantNotesButton
                                        prospectId={facture.prospect_id}
                                        prospectName={facture.client_nom || ''}
                                        variant="badge"
                                    />
                                    {facture.facture_type === 'sous_traitance' && (
                                        <span
                                            className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-bold bg-orange-700 text-white"
                                            title={`Sous-traitance${facture.sous_traitant_nom ? ' - ' + facture.sous_traitant_nom : ''}`}
                                            data-testid="facture-detail-st-badge"
                                        >
                                            ST
                                        </span>
                                    )}
                                    {facture.statut_envoi === 'envoye' && (
                                        <Badge className="text-[10px] bg-blue-100 text-blue-700 border border-blue-200 flex items-center gap-1">
                                            <CheckCircle2 className="w-3 h-3" /> Envoyée
                                        </Badge>
                                    )}
                                    <span className="text-xs text-gray-500">
                                        Prix TTC : <strong className="text-gray-900">{formatEUR(facture.prix_ttc)}</strong>
                                        {' · '}
                                        Solde dû : <strong className={facture.solde > 0 ? 'text-red-600' : 'text-emerald-600'}>{formatEUR(facture.solde)}</strong>
                                    </span>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Actions */}
                    <div className="flex items-center gap-2">
                        {facture.facture_type === 'sous_traitance' && (
                            <button
                                onClick={async () => {
                                    try {
                                        await axios.put(`${API_URL}/api/sous-traitance/factures/${facture.id}/toggle-paid`, {}, { withCredentials: true });
                                        await fetchData();
                                        toast.success('Statut paiement mis à jour');
                                    } catch {
                                        toast.error('Erreur mise à jour');
                                    }
                                }}
                                className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg border text-sm font-semibold transition-colors ${
                                    facture.paye
                                        ? 'border-red-200 bg-red-50 text-red-700 hover:bg-red-100'
                                        : 'border-emerald-200 bg-emerald-50 text-emerald-700 hover:bg-emerald-100'
                                }`}
                                data-testid="facture-st-toggle-paid"
                            >
                                {facture.paye ? <XCircle className="w-4 h-4" /> : <CheckCircle2 className="w-4 h-4" />}
                                {facture.paye ? 'Marquer non payée' : 'Marquer payée'}
                            </button>
                        )}
                        <button
                            onClick={handleDownload}
                            disabled={downloading}
                            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-gray-200 bg-white text-[#1B365D] text-sm font-semibold hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                            data-testid="facture-download-btn"
                        >
                            {downloading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
                            Télécharger
                        </button>
                        <button
                            onClick={handleSend}
                            disabled={sending || !canWriteFact}
                            title={!canWriteFact ? 'Accès refusé : permission Écriture requise' : ''}
                            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-[#1B365D] text-white text-sm font-semibold hover:bg-[#152a4a] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                            data-testid="facture-send-btn"
                        >
                            {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                            {facture.statut_envoi === 'envoye' ? 'Renvoyer' : 'Envoyer'}
                        </button>
                    </div>
                </div>

                {/* Important Notes Banner */}
                <ImportantNotesBanner prospectId={facture.prospect_id} />

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                    {/* Informations client */}
                    <section
                        className="bg-white rounded-xl border border-gray-200 shadow-sm p-5"
                        data-testid="facture-client-info"
                    >
                        <div className="flex items-center gap-2 mb-4">
                            <User className="w-4 h-4 text-[#C9A962]" />
                            <h2 className="font-semibold text-[#1B365D]">Informations client</h2>
                            {hasOverride && (
                                <Badge className="text-[10px] bg-amber-100 text-amber-700 border border-amber-200 ml-auto">Personnalisé</Badge>
                            )}
                        </div>
                        <dl className="divide-y divide-gray-100">
                            <div className="flex items-center justify-between py-2.5">
                                <dt className="text-sm text-gray-500 flex items-center gap-2">
                                    <User className="w-4 h-4" /> Nom
                                </dt>
                                <dd className="text-sm font-medium text-gray-900" data-testid="facture-client-nom">{displayNom}</dd>
                            </div>
                            <div className="flex items-center justify-between py-2.5">
                                <dt className="text-sm text-gray-500 flex items-center gap-2">
                                    <Phone className="w-4 h-4" /> Téléphone
                                </dt>
                                <dd className="text-sm font-medium text-gray-900" data-testid="facture-client-tel">
                                    {displayTel ? <a href={`tel:${displayTel}`} className="hover:text-[#1B365D]">{displayTel}</a> : '—'}
                                </dd>
                            </div>
                            <div className="flex items-center justify-between py-2.5">
                                <dt className="text-sm text-gray-500 flex items-center gap-2">
                                    <Mail className="w-4 h-4" /> Email
                                </dt>
                                <dd className="text-sm font-medium text-gray-900" data-testid="facture-client-email">
                                    {displayEmail ? <a href={`mailto:${displayEmail}`} className="hover:text-[#1B365D]">{displayEmail}</a> : '—'}
                                </dd>
                            </div>
                            <div className="flex items-center justify-between py-2.5">
                                <dt className="text-sm text-gray-500 flex items-center gap-2">
                                    <Receipt className="w-4 h-4" /> Prix TTC
                                </dt>
                                <dd className="text-sm font-semibold text-gray-900">{formatEUR(displayPrixTtc)}</dd>
                            </div>
                        </dl>
                    </section>

                    {/* Adresses — une seule sera imprimée sur la facture */}
                    <section
                        className="bg-white rounded-xl border border-gray-200 shadow-sm p-5"
                        data-testid="facture-adresses"
                    >
                        <div className="flex items-center gap-2 mb-1">
                            <MapPin className="w-4 h-4 text-[#C9A962]" />
                            <h2 className="font-semibold text-[#1B365D]">Adresse de prestation</h2>
                        </div>
                        <p className="text-xs text-gray-400 mb-3">Cochez l'adresse à faire apparaître sur la facture PDF</p>
                        <div className="space-y-3">
                            {/* Depart */}
                            <label
                                onClick={() => setSelectedAddress('depart')}
                                className={`flex items-start gap-3 rounded-lg border p-3 cursor-pointer transition-colors ${selected === 'depart' ? 'border-emerald-300 bg-emerald-50/40' : 'border-gray-200 hover:bg-gray-50'}`}
                                data-testid="facture-addr-depart"
                            >
                                <Checkbox
                                    checked={selected === 'depart'}
                                    onCheckedChange={() => setSelectedAddress('depart')}
                                    className="mt-0.5"
                                />
                                <div className="flex-1">
                                    <div className="flex items-center gap-2 text-emerald-700 mb-1">
                                        <Truck className="w-4 h-4" />
                                        <span className="text-xs font-semibold uppercase tracking-wide">Adresse de départ</span>
                                    </div>
                                    <p className="text-sm text-gray-900">
                                        {addrDepart || <span className="italic text-gray-400">Non renseignée</span>}
                                    </p>
                                </div>
                            </label>
                            {/* Arrivee */}
                            <label
                                onClick={() => setSelectedAddress('arrivee')}
                                className={`flex items-start gap-3 rounded-lg border p-3 cursor-pointer transition-colors ${selected === 'arrivee' ? 'border-blue-300 bg-blue-50/40' : 'border-gray-200 hover:bg-gray-50'}`}
                                data-testid="facture-addr-arrivee"
                            >
                                <Checkbox
                                    checked={selected === 'arrivee'}
                                    onCheckedChange={() => setSelectedAddress('arrivee')}
                                    className="mt-0.5"
                                />
                                <div className="flex-1">
                                    <div className="flex items-center gap-2 text-blue-700 mb-1">
                                        <Package className="w-4 h-4" />
                                        <span className="text-xs font-semibold uppercase tracking-wide">Adresse d'arrivée</span>
                                    </div>
                                    <p className="text-sm text-gray-900">
                                        {addrArrivee || <span className="italic text-gray-400">Non renseignée</span>}
                                    </p>
                                </div>
                            </label>
                            {facture.adresse_override && (
                                <p className="text-[11px] text-amber-700 italic">
                                    ⚠️ Adresse personnalisée active : « {facture.adresse_override} » (remplace la sélection pour le PDF)
                                </p>
                            )}
                        </div>
                    </section>
                </div>

                {/* ───── Section Paiements ───── */}
                <section
                    className="bg-white rounded-xl border border-gray-200 shadow-sm p-5 space-y-5"
                    data-testid="facture-payments-section"
                >
                    <div className="flex items-center justify-between flex-wrap gap-3">
                        <div className="flex items-center gap-2">
                            <CreditCard className="w-5 h-5 text-[#C9A962]" />
                            <h2 className="font-semibold text-[#1B365D]">Paiements & solde</h2>
                        </div>
                        <Badge className={`text-[10px] ${paiement.cls}`}>{paiement.label}</Badge>
                    </div>

                    {/* Progress bar */}
                    {(() => {
                        const total = Number(facture.prix_ttc || 0);
                        const paye = Number(facture.total_paye || 0);
                        const solde = Number(facture.solde || 0);
                        const pct = total > 0 ? Math.min((paye / total) * 100, 100) : 0;
                        const barColor = solde <= 0.01 ? 'bg-emerald-500' : paye > 0 ? 'bg-orange-400' : 'bg-red-400';
                        return (
                            <div className="space-y-2">
                                <div className="flex items-center justify-between text-sm">
                                    <span className="text-gray-500">Progression</span>
                                    <span className="font-semibold text-[#1B365D]" data-testid="facture-pct">{pct.toFixed(0)} %</span>
                                </div>
                                <div className="h-3 bg-gray-100 rounded-full overflow-hidden">
                                    <div
                                        className={`h-full ${barColor} transition-all duration-500`}
                                        style={{ width: `${pct}%` }}
                                    />
                                </div>
                                <div className="grid grid-cols-3 gap-3 mt-3">
                                    <div className="rounded-lg bg-gray-50 px-3 py-3 text-center">
                                        <div className="text-[10px] uppercase tracking-wide text-gray-500 mb-1">Total TTC</div>
                                        <div className="font-bold text-[#1B365D]">{formatEUR(total)}</div>
                                    </div>
                                    <div className="rounded-lg bg-emerald-50 border border-emerald-100 px-3 py-3 text-center">
                                        <div className="text-[10px] uppercase tracking-wide text-emerald-700 mb-1">Déjà versé</div>
                                        <div className="font-bold text-emerald-700" data-testid="facture-total-paye">{formatEUR(paye)}</div>
                                    </div>
                                    <div className={`rounded-lg px-3 py-3 text-center border ${solde > 0 ? 'bg-red-50 border-red-100' : 'bg-gray-50 border-gray-100'}`}>
                                        <div className={`text-[10px] uppercase tracking-wide mb-1 ${solde > 0 ? 'text-red-700' : 'text-gray-500'}`}>Reste à payer</div>
                                        <div className={`font-bold ${solde > 0 ? 'text-red-700' : 'text-gray-400'}`} data-testid="facture-solde">
                                            {formatEUR(solde)}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        );
                    })()}

                    {/* Historique */}
                    <div className="space-y-2">
                        <div className="text-sm font-semibold text-gray-700">Historique des paiements</div>
                        {facture.acomptes && facture.acomptes.length > 0 ? (
                            <ul className="divide-y divide-gray-100 border border-gray-100 rounded-lg" data-testid="facture-payments-list">
                                {facture.acomptes.map(a => {
                                    const mode = PAIEMENT_MODES.find(m => m.value === a.type_paiement);
                                    const Icon = mode?.icon || CreditCard;
                                    return (
                                        <li key={a.id} className="flex items-center gap-3 px-3 py-2.5 hover:bg-gray-50 transition-colors" data-testid={`payment-row-${a.id}`}>
                                            <div className="h-8 w-8 rounded-lg bg-[#1B365D]/5 flex items-center justify-center shrink-0">
                                                <Icon className="w-4 h-4 text-[#1B365D]" />
                                            </div>
                                            <div className="flex-1 min-w-0">
                                                <div className="flex items-center gap-2 flex-wrap">
                                                    <span className="font-semibold text-[#1B365D]">{formatEUR(a.montant)}</span>
                                                    <span className="text-xs text-gray-500">·</span>
                                                    <span className="text-xs text-gray-500">{formatDate(a.date)}</span>
                                                    {mode && (
                                                        <Badge className="text-[9px] bg-gray-100 text-gray-700 border border-gray-200 ml-1">{mode.label}</Badge>
                                                    )}
                                                </div>
                                                {a.remarque && <div className="text-xs text-gray-500 truncate mt-0.5">{a.remarque}</div>}
                                            </div>
                                            {isAdmin && (
                                                <button
                                                    onClick={() => deletePayment(a.id)}
                                                    className="p-1.5 rounded-md text-gray-400 hover:text-red-600 hover:bg-red-50 transition-colors"
                                                    title="Supprimer ce paiement (admin)"
                                                    data-testid={`payment-delete-${a.id}`}
                                                >
                                                    <Trash2 className="w-4 h-4" />
                                                </button>
                                            )}
                                        </li>
                                    );
                                })}
                            </ul>
                        ) : (
                            <p className="text-sm text-gray-400 italic px-1">Aucun paiement enregistré pour cette facture.</p>
                        )}
                    </div>

                    {/* Formulaire ajout paiement */}
                    {Number(facture.solde || 0) > 0.01 ? (
                        <div className="border-t border-gray-100 pt-4 space-y-3">
                            <div className="flex items-center gap-2">
                                <Plus className="w-4 h-4 text-[#C9A962]" />
                                <span className="text-sm font-semibold text-gray-700">Enregistrer un nouveau paiement</span>
                            </div>
                            <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                                <div>
                                    <Label className="text-xs" htmlFor="pay-montant">Montant (EUR)</Label>
                                    <div className="flex gap-2 mt-1">
                                        <Input
                                            id="pay-montant"
                                            type="number"
                                            step="0.01"
                                            min="0"
                                            placeholder="0.00"
                                            value={paymentForm.montant}
                                            onChange={e => setPaymentForm({ ...paymentForm, montant: e.target.value })}
                                            data-testid="pay-montant"
                                        />
                                    </div>
                                </div>
                                <div>
                                    <Label className="text-xs" htmlFor="pay-date">Date</Label>
                                    <Input
                                        id="pay-date"
                                        type="date"
                                        value={paymentForm.date}
                                        onChange={e => setPaymentForm({ ...paymentForm, date: e.target.value })}
                                        className="mt-1"
                                        data-testid="pay-date"
                                    />
                                </div>
                                <div className="md:col-span-2">
                                    <Label className="text-xs">Mode de paiement</Label>
                                    <div className="flex flex-wrap gap-1.5 mt-1">
                                        {PAIEMENT_MODES.map(m => {
                                            const Icon = m.icon;
                                            const active = paymentForm.type_paiement === m.value;
                                            return (
                                                <button
                                                    key={m.value}
                                                    type="button"
                                                    onClick={() => setPaymentForm({ ...paymentForm, type_paiement: m.value })}
                                                    className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium border transition-colors ${
                                                        active
                                                            ? 'bg-[#1B365D] text-white border-[#1B365D]'
                                                            : 'bg-white text-gray-600 border-gray-200 hover:border-[#1B365D]/40 hover:text-[#1B365D]'
                                                    }`}
                                                    data-testid={`pay-mode-${m.value}`}
                                                >
                                                    <Icon className="w-3.5 h-3.5" /> {m.label}
                                                </button>
                                            );
                                        })}
                                    </div>
                                </div>
                            </div>
                            <div>
                                <Label className="text-xs" htmlFor="pay-remarque">Remarque (optionnel)</Label>
                                <Input
                                    id="pay-remarque"
                                    placeholder="Ex: Paiement du solde à la livraison"
                                    value={paymentForm.remarque}
                                    onChange={e => setPaymentForm({ ...paymentForm, remarque: e.target.value })}
                                    className="mt-1"
                                    data-testid="pay-remarque"
                                />
                            </div>
                            <div className="flex items-center justify-between gap-2 flex-wrap pt-1">
                                <div className="flex items-center gap-1.5 text-xs">
                                    <span className="text-gray-500">Raccourcis :</span>
                                    <button
                                        type="button"
                                        onClick={() => fillPct(30)}
                                        className="px-2 py-1 rounded-md bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium transition-colors"
                                        data-testid="pay-pct-30"
                                    >30 %</button>
                                    <button
                                        type="button"
                                        onClick={() => fillPct(50)}
                                        className="px-2 py-1 rounded-md bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium transition-colors"
                                        data-testid="pay-pct-50"
                                    >50 %</button>
                                    <button
                                        type="button"
                                        onClick={fillSolde}
                                        className="px-2 py-1 rounded-md bg-emerald-100 hover:bg-emerald-200 text-emerald-700 font-semibold transition-colors"
                                        data-testid="pay-solder"
                                    >Solder ({formatEUR(facture.solde)})</button>
                                </div>
                                <button
                                    onClick={submitPayment}
                                    disabled={addingPayment}
                                    className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-[#1B365D] text-white text-sm font-semibold hover:bg-[#152a4a] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                    data-testid="pay-submit"
                                >
                                    {addingPayment ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
                                    Enregistrer le paiement
                                </button>
                            </div>
                        </div>
                    ) : (
                        <div className="border-t border-gray-100 pt-4 flex items-center gap-2 text-emerald-700 text-sm" data-testid="facture-fully-paid">
                            <CheckCircle2 className="w-5 h-5" />
                            <span className="font-semibold">Cette facture est totalement réglée.</span>
                        </div>
                    )}
                </section>

                {/* Bouton modifier informations */}
                <div className="flex items-center gap-3">
                    <button
                        onClick={openEdit}
                        className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border-2 border-dashed border-gray-300 text-gray-600 text-sm font-medium hover:border-[#C9A962] hover:text-[#C9A962] hover:bg-[#C9A962]/5 transition-all"
                        data-testid="facture-edit-btn"
                    >
                        <Pencil className="w-4 h-4" />
                        Modifier informations (pour cette facture uniquement)
                    </button>
                    {hasOverride && (
                        <button
                            onClick={resetOverrides}
                            className="text-xs text-gray-500 hover:text-red-600 underline transition-colors"
                            data-testid="facture-reset-btn"
                        >
                            Réinitialiser
                        </button>
                    )}
                </div>

                {/* Dialog */}
                <Dialog open={editOpen} onOpenChange={setEditOpen}>
                    <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="facture-edit-dialog">
                        <DialogHeader>
                            <DialogTitle>Modifier les informations de la facture</DialogTitle>
                            <p className="text-xs text-gray-500 mt-1">
                                Ces changements n'affectent que <strong>cette facture</strong>. Le client, le devis et le prospect ne seront pas modifiés.
                            </p>
                        </DialogHeader>
                        <div className="space-y-3 py-2">
                            <div>
                                <Label htmlFor="edit-nom">Nom</Label>
                                <Input id="edit-nom" value={editForm.nom} onChange={e => setEditForm({ ...editForm, nom: e.target.value })} data-testid="edit-nom" />
                            </div>
                            <div>
                                <Label htmlFor="edit-tel">Téléphone</Label>
                                <Input id="edit-tel" value={editForm.telephone} onChange={e => setEditForm({ ...editForm, telephone: e.target.value })} data-testid="edit-tel" />
                            </div>
                            <div>
                                <Label htmlFor="edit-email">Email</Label>
                                <Input id="edit-email" type="email" value={editForm.email} onChange={e => setEditForm({ ...editForm, email: e.target.value })} data-testid="edit-email" />
                            </div>
                            <div>
                                <Label htmlFor="edit-adresse">Adresse</Label>
                                <Input id="edit-adresse" value={editForm.adresse} onChange={e => setEditForm({ ...editForm, adresse: e.target.value })} data-testid="edit-adresse" placeholder="Adresse pour la facture" />
                            </div>

                            {/* Désignations */}
                            <div className="border-t border-gray-200 pt-3 mt-3">
                                <div className="flex items-center justify-between mb-2">
                                    <Label className="text-sm font-semibold">Désignations</Label>
                                    <button
                                        type="button"
                                        onClick={addLigne}
                                        className="text-xs px-2.5 py-1 rounded-lg bg-[#1B365D] text-white hover:bg-[#152a4a] flex items-center gap-1"
                                        data-testid="add-ligne-btn"
                                    >
                                        + Ajouter une ligne
                                    </button>
                                </div>
                                {(editForm.lignes || []).length === 0 ? (
                                    <p className="text-xs text-gray-500 italic">
                                        Aucune désignation — le tarif TTC ci-dessous sera utilisé tel quel.
                                        Cliquez sur "Ajouter une ligne" pour détailler la facture.
                                    </p>
                                ) : (
                                    <div className="space-y-1.5">
                                        {editForm.lignes.map((ligne, idx) => (
                                            <div key={idx} className="flex gap-2 items-center" data-testid={`ligne-row-${idx}`}>
                                                <Input
                                                    value={ligne.designation}
                                                    onChange={e => updateLigne(idx, 'designation', e.target.value)}
                                                    placeholder="Désignation (ex: Déménagement 15 m³)"
                                                    className="flex-1"
                                                    data-testid={`ligne-designation-${idx}`}
                                                />
                                                <Input
                                                    type="number"
                                                    step="0.01"
                                                    value={ligne.tarif_ttc}
                                                    onChange={e => updateLigne(idx, 'tarif_ttc', e.target.value)}
                                                    placeholder="Tarif TTC"
                                                    className="w-28"
                                                    data-testid={`ligne-tarif-${idx}`}
                                                />
                                                <button
                                                    type="button"
                                                    onClick={() => removeLigne(idx)}
                                                    className="p-1.5 rounded-lg text-red-500 hover:bg-red-50"
                                                    title="Supprimer cette ligne"
                                                    data-testid={`ligne-remove-${idx}`}
                                                >
                                                    <Trash2 className="w-4 h-4" />
                                                </button>
                                            </div>
                                        ))}
                                        <div className="flex justify-end pt-2 mt-2 border-t border-gray-100">
                                            <div className="text-sm">
                                                <span className="text-gray-600">Total TTC : </span>
                                                <span className="font-bold text-[#1B365D] text-base" data-testid="lignes-total">
                                                    {lignesTotal.toFixed(2)} €
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>

                            <div>
                                <Label htmlFor="edit-prix" className={hasLignes ? 'text-gray-400' : ''}>
                                    Tarif TTC (€) {hasLignes && <span className="text-xs italic">— calculé automatiquement à partir des désignations</span>}
                                </Label>
                                <Input
                                    id="edit-prix"
                                    type="number"
                                    step="0.01"
                                    value={hasLignes ? lignesTotal.toFixed(2) : editForm.prix_ttc}
                                    onChange={e => setEditForm({ ...editForm, prix_ttc: e.target.value })}
                                    disabled={hasLignes}
                                    className={hasLignes ? 'bg-gray-100 cursor-not-allowed' : ''}
                                    data-testid="edit-prix"
                                />
                            </div>
                        </div>
                        <DialogFooter>
                            <button
                                onClick={() => setEditOpen(false)}
                                className="px-4 py-2 rounded-lg border border-gray-200 bg-white text-sm hover:bg-gray-50"
                            >Annuler</button>
                            <button
                                onClick={saveEdit}
                                className="px-4 py-2 rounded-lg bg-[#1B365D] text-white text-sm font-semibold hover:bg-[#152a4a]"
                                data-testid="edit-save"
                            >Enregistrer</button>
                        </DialogFooter>
                    </DialogContent>
                </Dialog>
            </div>
        </Layout>
    );
}
